"use client";

import { addDays, format, Locale } from "date-fns";
import { enGB, fr } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { LuCalendar } from "react-icons/lu";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface IDatePickerProps {
    isPending: boolean;
    fieldValue: Date | undefined;
    onChange: (date: Date | undefined) => void;
}

export const DatePicker = ({ fieldValue, onChange, isPending }: IDatePickerProps) => {
    const t = useTranslations("expense");
    const locale = useLocale();

    const [ localeCalendar, setLocaleCalendar ] = useState<Locale>(fr);
    const [ isOpen, setIsOpen ] = useState(false);

    const handleSelect = (date: Date | undefined) => {
        onChange(date);
        setIsOpen(false);
    };

    useEffect(() => {
        if (locale === "en") {
            setLocaleCalendar(enGB);
        }
    }, [ locale ]);

    return (
        <FormItem className="flex flex-col items-center justify-between md:flex-row">
            <FormLabel className="text-lg">{t("date")} :</FormLabel>
            <div className="w-full md:w-1/2">
                <Popover open={ isOpen } onOpenChange={ setIsOpen }>
                    <PopoverTrigger asChild>
                        <FormControl>
                            <Button
                                disabled={ isPending }
                                variant="outline"
                                className={ cn("w-full justify-start text-left font-normal", !fieldValue && "text-muted-foreground") }
                            >
                                <LuCalendar />
                                {fieldValue ? format(fieldValue, "dd/MM/yyyy") : <span>{t("pickADate")}</span>}
                            </Button>
                        </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="flex w-auto flex-col space-y-2 p-2" align="start">
                        <Select
                            onValueChange={ value => {
                                const newDate = addDays(new Date(), parseInt(value));
                                handleSelect(newDate);
                            } }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Presets" />
                            </SelectTrigger>
                            <SelectContent position="popper">
                                <SelectItem value="-1">{t("yesterday")}</SelectItem>
                                <SelectItem value="0">{t("today")}</SelectItem>
                                <SelectItem value="1">{t("tomorrow")}</SelectItem>
                                <SelectItem value="3">{t("in3Days")}</SelectItem>
                                <SelectItem value="7">{t("inWeek")}</SelectItem>
                            </SelectContent>
                        </Select>
                        <Calendar
                            mode="single"
                            defaultMonth={ fieldValue }
                            selected={ fieldValue }
                            onSelect={ handleSelect }
                            disabled={ date => {
                                const currentYear = new Date().getFullYear();
                                const minDate = new Date(`${ currentYear - 5 }-01-01`);
                                const maxDate = new Date(`${ currentYear + 5 }-12-31`);
                                return date < minDate || date > maxDate;
                            } }
                            autoFocus
                            locale={ localeCalendar }
                        />
                    </PopoverContent>
                </Popover>
                <FormMessage />
            </div>
        </FormItem>
    );
};
